import React, { useRef, useState, useEffect } from 'react';
import {
  useStripe,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import {fontQuicksand} from './mixin';
import Styled from 'styled-components';
import { device } from '../../utils/responsive_utilities';
import GlobalSpinner from '../../utils/global-spinner';
import { REGEX_COLLECTION, getUserCountryCode, sendErrorToSentry } from '../../utils/web-helper';
import { computePricingInfo } from '../../../js_bytesize/templates/purchase/pricing-helper';
import { AttachPaymentMethodIdWithCustomer } from '../../services/parent-api';
import { orange, white } from '../shared-styles/Colors';
import { gpayKey } from '../../constant-data/constants';

export default function PaymentRequestButton({
  product,
  paymentHandler,
  settings,
  paymentSuccessHandlerForSVTrial,
  restrictionInfoStore,
  setGpayAvailable,
  fromT2PurchasePage,
  paymentRequest,
  setPaymentRequest,
  isGpayAvailable,
  isCouponApplied,
  coupon
}) {
    const [paymentRequestOptions, setPaymentRequestOptions] = useState(null);
    const stripe = useStripe();
    const errorElement = useRef(null);
    const [error, setError] = useState(null);
    const isEmailRequried =  settings &&  settings.currentUser &&  settings.currentUser.type == "guest_parent"
    const commonSubscriptionFailureEventName = 'Subscription via Payment Request Button Error';
    let commonEventProperties = {};
    // const isEmailRequried = skipSignUp && !settings.currentUser;

    /// Discount calculation of New Purchase Page
    const getBilledAmount = () => {
      let amount = product?.amount;
      if(isCouponApplied){
        if(coupon.percent_off){
          amount = amount * (100 - coupon.percent_off)/100;
        }else{
          amount = amount - (coupon.amount_off);
        }
      }
      return amount;
    }

    // This useEffect will be used to create or update PaymentRequest Object
    useEffect(() => {
      if (product && JSON.stringify(product) !== '{}') {
        const enablePaymentRequestButton = settings?.enable_payment_request_button;
        const clientSecret = settings?.client_secret;
        if(!enablePaymentRequestButton){
          setGpayStatus(false);
        }
        // We'll proceed only if enable_payment_request_button is true and stripe is available
        if (enablePaymentRequestButton && stripe && clientSecret) {
          let eventName = '';
          if(fromT2PurchasePage){
            const adjustedAmount = (settings?.adjustable_amount || 0);
            var finalAmount =  parseInt((getBilledAmount() - (adjustedAmount || 0)));
          }
          else{
          const info = computePricingInfo(product, settings);
          var finalAmount = Math.ceil(info.percentOff ? info.discountedPrice : info.actualPrice);
          }
          const currency = product.currency_code?.toLowerCase() || 'usd';
          const productName = product.name;
          const country_code = settings.country_code || 'US';
          if(!paymentRequest) {
            // Create new PaymentRequest object if not created.
            try{
              eventName = "Payment Request Object Created";
              const pr = stripe.paymentRequest({
                country: getUserCountryCode(),
                currency: currency,
                total: {
                  label: productName,
                  amount: finalAmount
                },
                requestPayerName: true,
                requestPayerEmail: true,
              });
              setPaymentRequest(pr);
            }
            catch(error){
              trackAnalyticsEvent("Payment Request Object Not Created", {
                'Enable Payment Request Button': enablePaymentRequestButton,
                'Stripe Available': stripe ? 'Yes' : 'No',
                'Client Secret Available': clientSecret ? 'Yes' : 'No',
              });
              let errorInfo = {
                extraMsg: 'Stripe payment request object initialisation failed',
              };
              sendErrorToSentry(error, errorInfo);
              setGpayAvailable && setGpayAvailable(false);
            }
            
          } else {
            // Update PaymentRequest object, with new product details.
            eventName = "Payment Request Object Updated";
            paymentRequest.update({
              currency: currency,
              total: {
                label: productName,
                amount: finalAmount
              },
            })
          }
          trackAnalyticsEvent(eventName, {
            'Product Name': productName,
            'Final Amount': finalAmount,
            'Currency': currency,
            'Country Code': country_code,
          });
        } else {
          trackAnalyticsEvent("Payment Request Object Not Created", {
            'Enable Payment Request Button': enablePaymentRequestButton,
            'Stripe Available': stripe ? 'Yes' : 'No',
            'Client Secret Available': clientSecret ? 'Yes' : 'No',
          });
        }
      }
    }, [product, stripe]);

    // This useEffect will be used to 
    // 1. Create or update PaymentRequestOptions, which will inturn be used to render PaymentRequestButton.
    // 2. Listen to PaymentRequestButton events and confirm Card Setup.
    useEffect(() => {
      if(paymentRequest) {
        const clientSecret = settings?.client_secret
        // Check the availability of the Payment Request API.
        if (!paymentRequestOptions) {
          paymentRequest.canMakePayment().then((result) => {
            let eventName = '';
            const googlePayAvaialbleEventPropertyValue = result && result.googlePay ? 'Yes' : 'No';
            const applePayAvaialbleEventPropertyValue = result && result.applePay ? 'Yes' : 'No';

            let properties = {
              'Google Pay Button Available': googlePayAvaialbleEventPropertyValue,
              'Apple Pay Button Available': applePayAvaialbleEventPropertyValue,
            };
            let buttonTheme = 'light-outline';
            //CTA Height: 6.4rem
            let buttonHeight = '64px';
            
            // NOTE: Right now, we are focussing on Google Pay only, as Apple Pay testing is not done yet,
            // due to unavailability of physical US credit card.
            if (result && result.googlePay) {
              eventName = 'Payment Request Button available';
              setGpayStatus(true);
              properties = {
                ...properties,
                'Google Pay Button Visible': 'Yes',
                'Button Theme': buttonTheme,
                'Button Height': buttonHeight,
              };
              setPaymentRequestOptions({
                style: {
                  paymentRequestButton: {
                    type: 'subscribe',
                    theme: buttonTheme,
                    height: buttonHeight,
                  },
                },
                paymentRequest: paymentRequest,
              });
            } else if (result && result.applePay){
              setGpayStatus(false)
              eventName = 'Payment Request Button not supported';
              setPaymentRequestOptions(null);
              properties = {
                ...properties,
                'Apple Pay Button Visible': 'No',
                'Apple Pay Block Reason': 'Testing with Apple Pay is blocked right now, due to unavailability of physical US credit card',
              };
            } else {
              setGpayStatus(false);
              eventName = 'Payment Request Button not available';
              properties = {
                ...properties,
                'Google Pay Button Visible': 'No',
                'Apple Pay Button Visible': 'No',
              }
              setPaymentRequestOptions(null);
            }
            setGpayStatus(false);
            trackAnalyticsEvent(eventName, properties);
          });
        }

        paymentRequest.on('paymentmethod', async (ev) => {
          const walletName = ev?.walletName;
          const payerEmail = ev?.payerEmail;
          const paymentMethodId = ev.paymentMethod.id;
          const commonPaymentFailureMessage = 'Payment failure occured. Please try again after some time.';
          commonEventProperties = {
            ...commonEventProperties,
            'Wallet Name': walletName,
            'Payer Email': payerEmail,
          };

          trackAnalyticsEvent('Initiated Payment request button details verification', commonEventProperties);

          if (clientSecret) {
            trackAnalyticsEvent('Client Secret found', commonEventProperties);
            verifyPaymentRequestButtonDetails(ev, clientSecret, paymentMethodId, walletName, payerEmail);
          } else {
            ev.complete('fail');
            console.error('Client Secret not found!');
            setError(commonPaymentFailureMessage);
            trackAnalyticsEvent('Client Secret not found', commonEventProperties);
          }
        });
      }
    }, [paymentRequest]);
    
    const verifyPaymentRequestButtonDetails = async (ev, clientSecret, paymentMethodId, walletName, payerEmail) => {
      const commonPaymentFailureMessage = 'Payment failure occured. Please try again after some time.';
      trackAnalyticsEvent(
        'Confirm Card Setup API Started for Payment Request Button',
        commonEventProperties
      );
      const { setupIntent, error } = await stripe.confirmCardSetup(
        clientSecret,
        { payment_method: paymentMethodId },
        { handleActions: false }
      );

      trackAnalyticsEvent(
        'Confirm Card Setup API Completed for Payment Request Button',
        commonEventProperties
      );

      let verifiedPaymentMethodId = null;

      trackAnalyticsEvent(
        'Payment Request Button Details Verification Started',
        commonEventProperties
      );

      if (error) {
        // This point will only be reached if there is an immediate error when
        // confirming the payment. Show error to your customer (for example, payment
        // details incomplete)
        if (error?.setup_intent?.status === 'succeeded') {
          ev.complete('success');
          verifiedPaymentMethodId = error?.setup_intent.payment_method;
          trackAnalyticsEvent(
            'Payment Request Button Details Verification Completed',
            {
              ...commonEventProperties,
              'Confirm Card Setup API Status': error.setup_intent.status,
            }
          );
        } else {
          setError(commonPaymentFailureMessage);
          ev.complete('fail');
          trackAnalyticsEvent(
            'Payment Request Button Details Verification Failed',
            {
              ...commonEventProperties,
              'Confirm Card Setup API Status': 'failure',
              'User Display Error': commonPaymentFailureMessage,
            }
          );
        }
      } else if (typeof setupIntent !== 'undefined' && setupIntent) {
        // Report to the browser that the confirmation was successful, prompting
        // it to close the browser payment method collection interface.
        ev.complete('success');

        // Check if the SetupIntent requires any actions and if so let Stripe.js
        // handle the flow. If using an API version older than "2019-02-11"
        // instead check for: `setupIntent.status === "requires_action"`.
        if (setupIntent.status === 'requires_action') {
          // Let Stripe.js handle the rest of the payment flow.
          const { error } = await stripe.confirmCardPayment(clientSecret);
          if (error) {
            // The payment failed -- ask your customer for a new payment method.
            const displayErrorMessage = 'Payment failure occured. Please try again with a new payment method.';
            setError(displayErrorMessage);
            trackAnalyticsEvent(
              'Payment Request Button Details Verification Failed',
              {
                ...commonEventProperties,
                'Confirm Card Setup API Status': 'failure',
                'User Display Error': displayErrorMessage,
              }
            );
          } else {
            // The payment has succeeded.
            trackAnalyticsEvent(
              'Payment Request Button Details Verification Completed',
              {
                ...commonEventProperties,
                'Confirm Card Setup API Status': setupIntent.status,
              }
            );
          }
        } else {
          // The payment has succeeded.
          trackAnalyticsEvent(
            'Payment Request Button Details Verification Completed',
            {
              ...commonEventProperties,
              'Confirm Card Setup API Status': setupIntent.status,
            }
          );
        }
        verifiedPaymentMethodId = setupIntent.payment_method;
      } else {
        ev.complete('fail');
        setError(commonPaymentFailureMessage);
        trackAnalyticsEvent(
          'Payment Request Button Details Verification Failed',
          {
            ...commonEventProperties,
            'Confirm Card Setup API Status': 'failure',
            'User Display Error': commonPaymentFailureMessage,
          }
        );
      }

      if (verifiedPaymentMethodId) {
        handlePaymentRequestButtonSubmit(verifiedPaymentMethodId, walletName, payerEmail);
      }
    };

    // Handle Payment Request Button Submit Action
    const handlePaymentRequestButtonSubmit = async (paymentMethodId, walletName, payerEmail) => {
      trackAnalyticsEvent(
        'Subscription Process via Payment Request Button Started',
        commonEventProperties
      );
      errorElement && errorElement.current ? errorElement.current.scrollIntoView({ behavior: "smooth" }) : null;
      try {
        const response = await attachPaymentMethodId(paymentMethodId);
        if (response.error) {
          // Inform the user if there was an error.
          setError(response.error.message);
        } else {
          setError(null);
          const result = {
            'paymentMethodId': paymentMethodId,
            'walletName': walletName,
            'payerEmail': payerEmail,
          }

          trackAnalyticsEvent('Parent Creation Required', {
            ...commonEventProperties,
            'Prop Value': restrictionInfoStore ? true : false,
          });
          if(restrictionInfoStore) {  
            const response = await parentCreation(payerEmail);
            result = {...response};
          }
          saveAndSubscribe(result);
        }
      } catch(e) {
        console.error(e);
        setError('Something went wrong, please try again');
        trackAnalyticsEvent(commonSubscriptionFailureEventName, {
          ...commonEventProperties,
          'Error type': 'Unexpected Error',
          'User Display Error': 'Something went wrong, please try again',
        });
      }
    };

    // API call to attach verified Payment Method Id with customer.
    const attachPaymentMethodId = async (paymentMethodId) => {
      try {
        const displayErrorMessage = 'We could not process your payment at this time';
        const commonErrorEventProperties = {
          ...commonEventProperties,
          'Error type': 'Payment Method Id not attached',
          'User Display Error': displayErrorMessage,
        };
        const params = {
          payment_method_id: paymentMethodId,
          rejectWithErrorObject: true,
        };
        trackAnalyticsEvent(
          'Attach Payment Method Id Api Call Started',
          commonEventProperties
        );
        const response = await AttachPaymentMethodIdWithCustomer(params);
        if (response.error) {
          console.error(response.error);
          setError(displayErrorMessage);
          trackAnalyticsEvent(commonSubscriptionFailureEventName, {
            ...commonErrorEventProperties,
            'API Error': JSON.stringify(response),            
          });
        }
        trackAnalyticsEvent('Attach Payment Method Id Api Call Completed', commonEventProperties);
        return response;
      } catch (error) {
        setError(displayErrorMessage);
        trackAnalyticsEvent(commonSubscriptionFailureEventName, commonErrorEventProperties);
        console.error(error);
        return error;
      }
    };

    const validateEmail = (email) =>{
      const displayErrorMessage = '';
      if (!email || email === '') {
        displayErrorMessage = "Couldn't find email from " + walletName + ' payment method. Please try again.';
        setError(displayErrorMessage);
        trackAnalyticsEvent(commonSubscriptionFailureEventName, {
          ...commonEventProperties,
          'Error type': 'Empty Email from Payment Intent',
          'User Display Error': displayErrorMessage,
        });
        return false;
      }
      if (!REGEX_COLLECTION.EMAIL_REGEX.test(email)) {
        displayErrorMessage = "Couldn't find valid email id from " + walletName + ' payment method. Please try again';
        setError(displayErrorMessage);
        trackAnalyticsEvent(commonSubscriptionFailureEventName, {
          ...commonEventProperties,
          'Error type': 'Wrong Email ID from Payment Intent',
          'User Display Error': displayErrorMessage,
        });
        return false;
      }
      return true;
    }

    const parentCreation = async (userEmail) => {
      if(!validateEmail(userEmail)) {
        GlobalSpinner.hide();
        return new Promise((resolve, reject) =>{
          return
        })
      }
      trackAnalyticsEvent('User Email Verified', commonEventProperties);
      let options = {
        user: {
          "email": userEmail,
          "type": 'parent',
          "time_zone": Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      }
      if(settings?.currentUser?.type === 'parent') {
        trackAnalyticsEvent('PTL Parent already available via Props', commonEventProperties);
        return new Promise((resolve, reject) => {
          resolve( {
            "via_remote_login" : true,
            "parent_id": settings?.currentUser?.id
          })
        })
      }
      return new Promise((resolve, reject) => {
        trackAnalyticsEvent('Create Parent and Add Child API Call Started', commonEventProperties);
        Config.DataApi.createParentAndAddChild(options, (data)=>{
          localStorage.removeItem('parentDobYear')
          if(data.success) {
            trackAnalyticsEvent('Create Parent and Add Child API Call Completed', commonEventProperties);
            resolve( {
              "via_remote_login" : true,
              "parent_id": data.parent_id
            })
          } else {
            const displayErrorMessage = data.email_error || '';
            GlobalSpinner.hide();
            trackAnalyticsEvent(commonSubscriptionFailureEventName, {
              ...commonEventProperties,
              'Error type': 'Create Parent and Add Child API Call Failed',
              'API Error': JSON.stringify(data),
              'User Display Error': displayErrorMessage,
            });
            return setError({error: displayErrorMessage});
          }
        })
      });
    }

    // This method is used to proceed with subscribe product api call by calling paymentHandler.
    const saveAndSubscribe = async (data) => {
      // this.setState({isSubmitDisabled: true});
      const walletName = data['walletName'];
      const email = data['payerEmail']
      let params = {};
      
      if (isEmailRequried) {
        if(!validateEmail(email)) {
          GlobalSpinner.hide();
          return;
        } else {
          params['emailId'] = email;
        }
      }

      trackAnalyticsEvent('Payment Handling for SVTrial', {
        ...commonEventProperties,
        'Prop Value': paymentSuccessHandlerForSVTrial ? true : false,
      });
      if (paymentSuccessHandlerForSVTrial) {
        try {
          trackAnalyticsEvent('Payment Handling for SVTrial API Started', {
            ...commonEventProperties,
          });
          await paymentSuccessHandlerForSVTrial(!settings.currentUser ? email : null, true);
          trackAnalyticsEvent('Payment Handling for SVTrial API Completed', {
            ...commonEventProperties,
          });
        } catch(err) {
          const errMsg = err && err.errors && err.errors[0] || err.msg || `Something went wrong, please try again`;
          console.error(err);
          setError(errMsg)
          trackAnalyticsEvent(commonSubscriptionFailureEventName, {
            ...commonEventProperties,
            'Error type': 'Payment Handling for SVTrial API Failed',
            'API Error': JSON.stringify(err),
            'User Display Error': displayErrorMessage,
          });
          return;
        }
      }
  
      const subscribeData = {
        'subscribe_via_payment_request_button': true,
        'wallet_name': walletName,
      };

      trackAnalyticsEvent('Subscribe Product API Call Started', {
        ...commonEventProperties,
        "Subscribe Product Data Params": JSON.stringify(subscribeData),
        "Params": JSON.stringify(params),
      });
      paymentHandler(subscribeData, params);
    }

    const setGpayStatus = (value) => {
      if(setGpayAvailable && isGpayAvailable == null && !localStorage.getItem(gpayKey)){
        localStorage.setItem(gpayKey, value);
        setGpayAvailable(value);
      }
    }

    return (<div>
      {(paymentRequestOptions) &&
        <PaymentRequestButtonWrapper className='payment-request-button-wrapper'>
          <PaymentRequestButtonCTAWrapper className='payment-request-button-cta-wrapper'>
            <PaymentRequestButtonElement options={paymentRequestOptions} />
          </PaymentRequestButtonCTAWrapper>
        <PaymentRequestButtonError role="alert" ref={errorElement}>{error}</PaymentRequestButtonError>
        <Separator />
      </PaymentRequestButtonWrapper>
      }
    </div>)
}

function Separator(){
  return (
    <PaymentRequestButtonSeparatorDiv className='payment-request-button-separator-div'>
      <PaymentRequestButtonSeparatorLine className='payment-request-button-separator-line'/>
      <PaymentRequestButtonSeparatorText className='payment-request-button-separator-text'>{' '}Or pay with cards{' '}</PaymentRequestButtonSeparatorText>
      <PaymentRequestButtonSeparatorLine className='payment-request-button-separator-line'/>
    </PaymentRequestButtonSeparatorDiv>
  )
}

export const PaymentRequestButtonWrapper = Styled.div`
  margin: 2rem auto 1rem;
  width: 47rem;
  @media ${device.PhoneLargeScreen} {
    width: 37rem;
  }
  @media ${device.Phone} {
    width: 34rem;
  }
`;

export const PaymentRequestButtonCTAWrapper = Styled.div`
  width: 100%;
  margin: 0 auto;
`;

export const PaymentRequestButtonError = Styled.p`
  color: ${orange};
  ${fontQuicksand(1.2, 1.6, 5)};
  min-height: 1rem;
`;

export const PaymentRequestButtonSeparatorDiv = Styled.div`
  margin-top: 2rem;
  display: flex;
  padding: 0 2rem;
  justify-content: space-between;
  align-items: center;
`;

export const PaymentRequestButtonSeparatorLine = Styled.div`
  background-color: ${white};
  height: 0.1rem;
  max-width: 35%;
  width: 35%;
  @media ${device.PhoneLargeScreen} {
    max-width: 33%;
  }
  @media ${device.Phone} {
    max-width: 30%;
  }
`;

export const PaymentRequestButtonSeparatorText = Styled.div`
  color: ${white};
  ${fontQuicksand(1.2, 1.6, 4)};
  text-align: center;
  margin: 1rem 0;
  @media ${device.PhoneLargeScreen} {
    ${fontQuicksand(1, 1, 4)};
  }
`;